import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@design-system/Dialog';
import { Skeleton } from '@design-system/Skeleton';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  usePaymentUpdateRedirect,
  type PaymentUpdateErrorKind,
} from './hooks/usePaymentUpdateRedirect';
import {
  useGetPaymentMethodV2,
  useSendPaymentMethodEmail,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import { QUERY_KEYS } from '@viome/viome-base/src/api/config';
import { SMPDialogContent, SMPMutationErrorDialog } from './components';
import { SMPSavedCardTile } from './components/SMPSavedCardTile';
import { SMPPaypalTile } from './components/SMPPaypalTile';
import {
  getPaymentMethodKind,
  formatLastChargeDate,
} from './utils/paymentMethodHelpers';
import {
  readReturnOutcome,
  stripReturnParams,
  type PaymentUpdateOutcome,
} from './utils/paymentUpdateReturn';
import { PaymentMethodKind } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { logSMPMutationError } from '@/utils/sentryLogger';

const TRIGGER_EMAIL_METHOD = 'trigger_email';
const NOTICE_DURATION_MS = 10000;

export const SMPPaymentMethodsPage = () => {
  const { smpGoBack } = useSMPNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const {
    subscriptionId,
    subscriptionDetail,
    queryParams,
    showMaintenance,
    isSessionReady,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: {
      discounts: true,
      changePlan: true,
      uiMessages: true,
      canSkip: true,
      holdingPattern: true,
    },
  });
  useMaintenanceGuard(showMaintenance);

  const queryEnabled =
    isSessionReady && !!queryParams.subscriptionId && !!queryParams.customerId;
  const paymentQueryParams = {
    subscriptionId: Number(queryParams.subscriptionId),
    customerId: String(queryParams.customerId),
  };

  const { data: paymentMethod, isLoading: isLoadingPayment } = useGetPaymentMethodV2(
    paymentQueryParams,
    { enabled: queryEnabled },
  );

  const sendEmailMutation = useSendPaymentMethodEmail();
  const paymentUpdate = usePaymentUpdateRedirect(paymentQueryParams, {
    onError: (error) => {
      logSMPMutationError('paymentUpdateUrl', error, {
        screen: 'SMPPaymentMethodsPage',
        subscriptionId,
      });
    },
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [returnNotice, setReturnNotice] = useState<PaymentUpdateOutcome | null>(null);

  // Loop sends the browser back here with `cardAddEvent=…`. Read it once,
  // show the outcome, refetch the card, and strip Loop's params so a refresh
  // doesn't repeat the message. `subscriptionId` / iframe params are kept.
  useEffect(() => {
    const outcome = readReturnOutcome(searchParams);
    if (!outcome) return;
    setReturnNotice(outcome);
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SMP_PAYMENT_METHOD_V2] });
    setSearchParams(stripReturnParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  useEffect(() => {
    if (!returnNotice) return;
    const timer = setTimeout(() => setReturnNotice(null), NOTICE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [returnNotice]);

  const goBack = () => smpGoBack();
  const isLoading = isSMPLoading || isLoadingPayment;

  if (isLoading) {
    return (
      <section
        className="page-container flex flex-col gap-12 pb-24"
        data-testid="smp-paymentMethodsPage-loading"
      >
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-12 w-80 rounded-xl" />
      </section>
    );
  }

  if (isSMPError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage variant="light" errorType="error" hideBackground hideNavBar />
    );
  }

  const kind = getPaymentMethodKind(paymentMethod);
  const lastChargeDate =
    paymentMethod?.last_charge_date
      ? formatLastChargeDate(paymentMethod.last_charge_date)
      : formatLastChargeDate(subscriptionDetail.last_payment_datetime);

  const handleEmailLink = () => {
    if (paymentMethod?.update_method === TRIGGER_EMAIL_METHOD || !paymentMethod?.update_url) {
      sendEmailMutation.mutate(queryParams, {
        onSuccess: () => setShowConfirmDialog(true),
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('sendPaymentMethodEmail', error, {
            screen: 'SMPPaymentMethodsPage',
            subscriptionId,
          });
        },
      });
    } else if (paymentMethod?.update_url) {
      window.open(paymentMethod.update_url, '_blank');
    }
  };

  const handleUpdateCard = () => {
    // A stale outcome message shouldn't sit next to a fresh attempt. A
    // previous error clears itself: `start()` resets the mutation.
    setReturnNotice(null);
    paymentUpdate.start();
  };

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-paymentMethodsPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <h1
          className="typography-h6 text-primary"
          data-testid="smp-paymentMethods-title"
        >
          Payment Information
        </h1>
      </div>

      <div className="flex max-w-2xl flex-col gap-6">
        <div className="flex flex-col gap-6 min-w-0">
          {returnNotice === 'success' && (
            <div
              data-testid="smp-paymentMethods-successToast"
              role="status"
              aria-live="polite"
              className="flex items-center gap-3 rounded-xl bg-foreground p-4 text-white"
            >
              <span
                data-testid="smp-paymentMethods-successToast-check"
                aria-hidden="true"
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#22c55e] text-white"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </span>
              <span className="typography-body font-medium">Card updated</span>
            </div>
          )}

          {returnNotice === 'unchanged' && (
            <div
              data-testid="smp-paymentMethods-unchangedNotice"
              role="status"
              aria-live="polite"
              className="rounded-xl border border-stroke-primary-subtle bg-white p-4 typography-body text-secondary"
            >
              Your card wasn&apos;t changed.
            </div>
          )}

          {kind === PaymentMethodKind.CARD && paymentMethod && (
            <>
              <SMPSavedCardTile
                paymentMethod={paymentMethod}
                lastChargeDate={lastChargeDate !== '—' ? lastChargeDate : undefined}
                onUpdateClick={handleUpdateCard}
                isUpdating={paymentUpdate.isPending}
              />
              {paymentUpdate.errorKind && (
                <PaymentUpdateErrorBanner
                  kind={paymentUpdate.errorKind}
                  isEmailPending={sendEmailMutation.isPending}
                  onEmailLink={handleEmailLink}
                />
              )}
              <EmailLinkButton
                isPending={sendEmailMutation.isPending}
                onClick={handleEmailLink}
              />
            </>
          )}

          {kind === PaymentMethodKind.PAYPAL && paymentMethod && (
            <>
              <SMPPaypalTile
                paymentMethod={paymentMethod}
                lastChargeDate={lastChargeDate !== '—' ? lastChargeDate : undefined}
              />
              <p
                className="typography-body text-secondary max-w-xl"
                data-testid="smp-paymentMethods-paypalCopy"
              >
                Your subscription is paid via PayPal. To change anything about this payment
                method, we&apos;ll email you a secure link.
              </p>
              <EmailLinkButton
                isPending={sendEmailMutation.isPending}
                onClick={handleEmailLink}
              />
            </>
          )}

          {(kind === PaymentMethodKind.UNKNOWN || !paymentMethod) && (
            <EmailLinkButton
              isPending={sendEmailMutation.isPending}
              onClick={handleEmailLink}
            />
          )}
        </div>
      </div>

      <Dialog
        open={showConfirmDialog}
        onOpenChange={(isOpen) => !isOpen && setShowConfirmDialog(false)}
      >
        <SMPDialogContent>
          <DialogHeader className="gap-6 text-start">
            <DialogTitle className="typography-h3 text-primary">
              Please check your inbox!
            </DialogTitle>
            <DialogDescription className="typography-body text-secondary">
              For security purposes we sent you an email message with a link to update your
              billing information.
            </DialogDescription>
          </DialogHeader>
        </SMPDialogContent>
      </Dialog>

      <SMPMutationErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        description="Something went wrong while sending the payment update email. Please try again."
      />
    </section>
  );
};

interface EmailLinkButtonProps {
  isPending: boolean;
  onClick: () => void;
}

const EmailLinkButton = ({ isPending, onClick }: EmailLinkButtonProps) => (
  <Button
    onClick={onClick}
    disabled={isPending}
    className="self-start"
    data-testid="smp-paymentMethods-emailBtn"
  >
    {isPending ? 'Sending...' : 'Email me a link to change payment info'}
  </Button>
);

interface PaymentUpdateErrorBannerProps {
  kind: PaymentUpdateErrorKind;
  isEmailPending: boolean;
  onEmailLink: () => void;
}

/**
 * Inline error under the card tile. `unavailable` (Loop/backend down) offers
 * the email-link fallback inline; `generic` just asks to retry.
 */
const PaymentUpdateErrorBanner = ({
  kind,
  isEmailPending,
  onEmailLink,
}: PaymentUpdateErrorBannerProps) => (
  <div
    data-testid={`smp-paymentMethods-updateError-${kind}`}
    role="alert"
    aria-live="polite"
    className="flex items-start gap-3 rounded-xl bg-[#fdecec] p-4 text-[#da1c2c]"
  >
    <div>
      <div className="typography-body font-medium">
        {kind === 'unavailable'
          ? "Couldn't open the payment page."
          : 'Something went wrong.'}
      </div>
      <div className="typography-caption">
        {kind === 'unavailable' ? (
          <>
            Please try again, or use the{' '}
            <button
              type="button"
              onClick={onEmailLink}
              disabled={isEmailPending}
              data-testid="smp-paymentMethods-updateError-emailLink"
              className="underline underline-offset-2 hover:no-underline disabled:opacity-60"
            >
              {isEmailPending ? 'Sending email...' : '“Email me a link”'}
            </button>{' '}
            option to update your card securely.
          </>
        ) : (
          'Please try again.'
        )}
      </div>
    </div>
  </div>
);
